import React, { useState, useEffect } from 'react';
import WizardSteps from '../common/WizardSteps';
import { useScaleData } from '../../hooks/useScaleData';

const RegisterTruckScreen = ({ state, setState, devices, onNavigate, config }) => {
    const [primaryCamera, setPrimaryCamera] = useState(null);
    const [configuredCameras, setConfiguredCameras] = useState([]);
    const [isOnline, setIsOnline] = useState(true);
    const [syncStatus, setSyncStatus] = useState({ trucksCount: 0, ticketsCount: 0, hasPending: false });

    const { 
		currentWeight, 
		weightStatus,
        setupScaleDataListener, 
		cleanupScaleDataListener,
		handleAutoConnect
	} = useScaleData(config?.scaleType);

    // Set up scale data listener when scale connection state changes
	useEffect(() => {
		if (devices.scale.connected) {
			// console.log('🏠 HomeScreen: Scale connected via devices prop');
			setupScaleDataListener();
		} else {
			// console.log('🏠 HomeScreen: Scale disconnected via devices prop');
			cleanupScaleDataListener();
		}
	}, [devices.scale.connected, setupScaleDataListener, cleanupScaleDataListener]);

    // Check network status on mount and periodically
    useEffect(() => {
        const checkNetwork = async () => {
            if (window.electronAPI && window.electronAPI.checkNetworkStatus) {
                const status = await window.electronAPI.checkNetworkStatus();
                setIsOnline(status.online);
            }
        };

        // Check immediately
        checkNetwork();

        // Check every 30 seconds
        const interval = setInterval(checkNetwork, 30000);

        return () => clearInterval(interval);
    }, []);

    // Check sync status on mount
    useEffect(() => {
        const checkSyncStatus = async () => {
            if (window.electronAPI && window.electronAPI.secureStorage) {
                const status = await window.electronAPI.secureStorage.getSyncQueueStatus();
                setSyncStatus(status);
            }
        };

        checkSyncStatus();

        // Listen for sync completion events
        if (window.electronAPI && window.electronAPI.onMessage) {
            window.electronAPI.onMessage('sync-completed', (status) => {
                setSyncStatus(status);
            });
        }
    }, []);

    // Set up scale data listener and camera stream when component mounts
    useEffect(() => {
        // Find primary camera and start stream
        if (devices.cameras && devices.cameras.length > 0) {
            let configured = [];

            // Start camera streams for all configured cameras
            devices.cameras.forEach((camera, index) => {
                if (camera.mjpegUrl && window.camera && window.camera.start) {

                    // Set up camera frame listener BEFORE starting the camera
                    window.camera.onFrame(camera.id, (jpegBase64) => {
                        // // console.log(`🏠 HomeScreen: Received frame from camera ${camera.id}`);
                        const cameraElements = document.querySelectorAll(`[data-camera-id="${camera.id}"]`);
                        cameraElements.forEach(el => {
                            el.src = `data:image/jpeg;base64,${jpegBase64}`;
                        });
                    });

                    // Start the camera stream
                    window.camera.start(
                        camera.mjpegUrl,
                        camera.username || '',
                        camera.password || '',
                        camera.id,
                        camera.authType || 'none'
                    );
                    if (camera.isPrimary) {
                        setPrimaryCamera(camera);
                    } else {
                        configured.push(camera);
                    }
                }
            });

            if (configured) {
                setConfiguredCameras(configured);
            }
        }

        // Cleanup function to remove event listeners and stop camera
        return () => {
            if (window.electronAPI && window.electronAPI.removeAllListeners) {
                // console.log('🚛 RegisterTruckScreen: Cleaning up scale data listener');
                window.electronAPI.removeAllListeners('scale-data');
            }

            // Stop camera stream when component unmounts
            if (primaryCamera && window.camera && window.camera.stop) {
                // console.log('🚛 RegisterTruckScreen: Stopping camera stream');
                window.camera.stop(primaryCamera.id);
            }

            if (configuredCameras.length > 0 && window.camera && window.camera.stop) {
                configuredCameras.forEach(camera => {
                    // console.log('🏠 HomeScreen: Stopping camera stream:', camera.name);
                    window.camera.stop(camera.id);
                });
            }
        };
    }, [devices.scale.connected, devices.cameras]);

    const steps = ['Capture License Plate', 'Confirm Details'];

    const captureImage = async () => {
        try {
            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();

            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                alert('Company and location must be configured before registering trucks. Please go to Configuration and select a company and location.');
                return;
            }

            // Handle currentWeight whether it's a string or number
            let numericWeight = 0;
            if (currentWeight) {
                if (typeof currentWeight === 'string') {
                    numericWeight = parseFloat(currentWeight.replace(/[^\d.-]/g, '')) || 0;
                } else {
                    numericWeight = parseFloat(currentWeight) || 0;
                }
            }

            // OFFLINE MODE - Skip camera requirement
            if (!isOnline) {
                // console.log('📴 Offline mode - skipping camera capture');
                
                // Go directly to step 2 without image capture
                setState({
                    ...state,
                    step: 2,
                    plateImage: null, // No image in offline mode
                    detectedPlate: '',
                    confirmedPlate: '',
                    truckNumber: '',
                    dotNumber: '',
                    companyName: '',
                    tareWeight: numericWeight.toFixed(2) || '0.00',
                    isProcessing: false // No OCR processing needed offline
                });
                return;
            }

            // ONLINE MODE - Camera is optional, but warn if not available
            if (!primaryCamera) {
                
                // Go directly to step 2 without image capture
                setState({
                    ...state,
                    step: 2,
                    plateImage: null, // No image without camera
                    detectedPlate: '',
                    confirmedPlate: '',
                    truckNumber: '',
                    dotNumber: '',
                    companyName: '',
                    tareWeight: numericWeight.toFixed(2) || '0.00',
                    isProcessing: false // No OCR processing needed without camera
                });
                return;
            }

            // Get the current camera image element
            const cameraElement = document.querySelector(`[data-camera-id="${primaryCamera.id}"]`);
            if (!cameraElement || !cameraElement.src || cameraElement.src === '') {
                alert('Camera feed not available. Please wait for the camera to load or check your camera configuration.');
                return;
            }

            // Capture the current frame from the camera
            const capturedImage = cameraElement.src;
            // console.log('📸 Captured image from camera:', primaryCamera.name);

            // Immediately transition to confirm screen with loading state
            setState({
                ...state,
                step: 2,
                plateImage: capturedImage,
                detectedPlate: '',
                confirmedPlate: '',
                truckNumber: '',
                dotNumber: '',
                companyName: '',
                tareWeight: numericWeight.toFixed(2) || '0.00',
                isProcessing: true // Add loading state
            });

            // Send image and weight to API for license plate detection
            const plateDetectionData = {
                images: [{
                    cameraId: primaryCamera.id,
                    cameraName: primaryCamera.name,
                    imageData: capturedImage, // Base64 image data
                    timestamp: new Date().toISOString()
                }],
                tareweight: numericWeight.toFixed(2),
                companyid: companyData.Id
            };

            // console.log('📤 Sending data for license plate detection:', plateDetectionData);

            try {
                const plateDetectionResponse = await window.electronAPI.request(
                    `/companies/${companyData.Id}/registeredtrucks`,
                    'POST',
                    plateDetectionData,
                    {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                );

                let responseData = null;
                if (plateDetectionResponse.ok) {
                    responseData = await plateDetectionResponse.body;
                    // console.log('✅ License plate detection response:', responseData);

                    // Update state with detected data
                    setState(prev => ({
                        ...prev,
                        detectedPlate: responseData?.NumberPlate || '',
                        confirmedPlate: responseData?.NumberPlate || '',
                        truckNumber: responseData?.TruckNumber || '',
                        dotNumber: responseData?.DotNumber || '',
                        companyName: responseData?.CompanyName || '',
                        tareWeight: numericWeight.toFixed(2) || '0.00',
                        isProcessing: false // Remove loading state
                    }));
                } else {
                    console.error('❌ Failed to detect license plate:', plateDetectionResponse.statusText);
                    // Don't show alert, just let user enter manually
                    // Reset to step 1 on failure
                    setState(prev => ({
                        ...prev,
                        detectedPlate: '',
                        confirmedPlate: '',
                        isProcessing: false
                    }));
                }
            } catch (error) {
                console.error('❌ Error during license plate detection:', error);
                // Don't show alert, just let user enter manually
                // Keep at step 2 but clear loading state
                setState(prev => ({
                    ...prev,
                    detectedPlate: '',
                    confirmedPlate: '',
                    isProcessing: false
                }));
            }
        } catch (error) {
            console.error('Failed to capture image:', error);
            alert('Failed to capture license plate image. Please try again.');
        }
    };

    const saveTruck = async () => {
        try {
            // Validate required fields
            if (!state.confirmedPlate.trim()) {
                alert('Please enter a valid license plate number');
                return;
            }

            // Get company data from secure storage
            const companyData = await window.electronAPI.secureStorage.getCompany();

            if (!companyData || !companyData.Id) {
                console.error('❌ No company data available');
                alert('Company data not available. Please check your configuration.');
                return;
            }

            // Prepare truck data
            const truckData = {
                numberplate: state.confirmedPlate,
                trucknumber: state.truckNumber || '',
                dotnumber: state.dotNumber || '',
                companyname: state.companyName || '',
                tareweight: parseFloat(state.tareWeight) || 0,
                status: 'active',
                companyid: companyData.Id
            };

            // OFFLINE MODE - Save locally
            if (!isOnline) {
                // console.log('📴 Offline mode - saving truck locally:', truckData);
                
                const savedTruck = await window.electronAPI.secureStorage.addPendingTruck(truckData);
                
                if (savedTruck) {
                    // Add to truck cache immediately for offline ticket creation
                    try {
                        const cachedTrucks = await window.electronAPI.secureStorage.getCachedRegisteredTrucks();
                        const newCachedTruck = {
                            Id: savedTruck.localId,
                            NumberPlate: savedTruck.numberplate,
                            TruckNumber: savedTruck.trucknumber,
                            TareWeight: savedTruck.tareweight,
                            DOTNumber: savedTruck.dotnumber,
                            CompanyName: savedTruck.companyname,
                            Status: 'pending_sync'
                        };
                        
                        const updatedCache = [...cachedTrucks.data, newCachedTruck];
                        await window.electronAPI.secureStorage.setCachedRegisteredTrucks(updatedCache);
                        // console.log('✅ Truck cache updated with locally saved truck');
                    } catch (cacheError) {
                        console.error('⚠️ Failed to update truck cache:', cacheError);
                    }

                    alert(`Truck ${state.confirmedPlate} saved locally!\n\nIt will be synced to the server when internet connection is restored.`);
                    
                    // Update sync status
                    const status = await window.electronAPI.secureStorage.getSyncQueueStatus();
                    setSyncStatus(status);
                    
                    // Reset state and navigate back to home
                    setState({
                        step: 1,
                        plateImage: null,
                        detectedPlate: '',
                        confirmedPlate: '',
                        truckNumber: '',
                        dotNumber: '',
                        companyName: '',
                        tareWeight: 0.00
                    });
                    onNavigate('home');
                } else {
                    alert('Failed to save truck locally. Please try again.');
                }
                return;
            }

            // ONLINE MODE - Send to API
            // console.log('📤 Saving truck data to API:', truckData);

            const saveResponse = await window.electronAPI.request(
                `/companies/${companyData.Id}/registeredtrucks/${state.confirmedPlate}`,
                'PATCH',
                truckData,
                {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            );

            if (saveResponse.ok) {
                const responseData = await saveResponse.body;
                // console.log('✅ Truck saved successfully:', responseData);

                // Update truck cache with newly registered truck
                try {
                    const cachedTrucks = await window.electronAPI.secureStorage.getCachedRegisteredTrucks();
                    const newTruck = {
                        Id: responseData.Id,
                        NumberPlate: responseData.NumberPlate || state.confirmedPlate,
                        TruckNumber: responseData.TruckNumber || state.truckNumber,
                        TareWeight: responseData.TareWeight || parseFloat(state.tareWeight),
                        DOTNumber: responseData.DOTNumber || state.dotNumber,
                        CompanyName: responseData.CompanyName || state.companyName,
                        Status: 'active'
                    };
                    
                    // Add to cache if not already there
                    if (!cachedTrucks.data.find(t => t.Id === newTruck.Id)) {
                        const updatedCache = [...cachedTrucks.data, newTruck];
                        await window.electronAPI.secureStorage.setCachedRegisteredTrucks(updatedCache);
                        // console.log('✅ Truck cache updated with newly registered truck');
                    }
                } catch (cacheError) {
                    console.error('⚠️ Failed to update truck cache:', cacheError);
                    // Don't fail the registration if cache update fails
                }

                alert(`Truck ${state.confirmedPlate} registered successfully!`);

                // Reset state and navigate back to home
                setState({
                    step: 1,
                    plateImage: null,
                    detectedPlate: '',
                    confirmedPlate: '',
                    truckNumber: '',
                    dotNumber: '',
                    companyName: '',
                    tareWeight: 0.00
                });
                onNavigate('home');
            } else {
                console.error('❌ Failed to save truck:', saveResponse.statusText);
                // console.log('❌ Failed to save truck:', saveResponse);
                
                // If API fails, offer to save locally
                const saveLocally = window.confirm(
                    'Failed to register truck with server. Would you like to save it locally and sync later?'
                );
                
                if (saveLocally) {
                    const savedTruck = await window.electronAPI.secureStorage.addPendingTruck(truckData);
                    if (savedTruck) {
                        // Add to truck cache immediately
                        try {
                            const cachedTrucks = await window.electronAPI.secureStorage.getCachedRegisteredTrucks();
                            const newCachedTruck = {
                                Id: savedTruck.localId,
                                NumberPlate: savedTruck.numberplate,
                                TruckNumber: savedTruck.trucknumber,
                                TareWeight: savedTruck.tareweight,
                                DOTNumber: savedTruck.dotnumber,
                                CompanyName: savedTruck.companyname,
                                Status: 'pending_sync'
                            };
                            
                            const updatedCache = [...cachedTrucks.data, newCachedTruck];
                            await window.electronAPI.secureStorage.setCachedRegisteredTrucks(updatedCache);
                            // console.log('✅ Truck cache updated with locally saved truck (fallback)');
                        } catch (cacheError) {
                            console.error('⚠️ Failed to update truck cache:', cacheError);
                        }

                        alert(`Truck ${state.confirmedPlate} saved locally!\n\nIt will be synced to the server when possible.`);
                        
                        // Update sync status
                        const status = await window.electronAPI.secureStorage.getSyncQueueStatus();
                        setSyncStatus(status);
                        
                        // Reset state and navigate back to home
                        setState({
                            step: 1,
                            plateImage: null,
                            detectedPlate: '',
                            confirmedPlate: '',
                            truckNumber: '',
                            dotNumber: '',
                            companyName: '',
                            tareWeight: 0.00
                        });
                        onNavigate('home');
                    }
                }
            }
        } catch (error) {
            console.error('Failed to save truck:', error);
            
            // If error occurs, offer to save locally
            const saveLocally = window.confirm(
                'Failed to register truck with server. Would you like to save it locally and sync later?'
            );
            
            if (saveLocally && window.electronAPI && window.electronAPI.secureStorage) {
                const companyData = await window.electronAPI.secureStorage.getCompany();
                const truckData = {
                    numberplate: state.confirmedPlate,
                    trucknumber: state.truckNumber || '',
                    dotnumber: state.dotNumber || '',
                    companyname: state.companyName || '',
                    tareweight: parseFloat(state.tareWeight) || 0,
                    status: 'active',
                    companyId: companyData.Id
                };
                
                const savedTruck = await window.electronAPI.secureStorage.addPendingTruck(truckData);
                if (savedTruck) {
                    // Add to truck cache immediately
                    try {
                        const cachedTrucks = await window.electronAPI.secureStorage.getCachedRegisteredTrucks();
                        const newCachedTruck = {
                            Id: savedTruck.localId,
                            NumberPlate: savedTruck.numberplate,
                            TruckNumber: savedTruck.trucknumber,
                            TareWeight: savedTruck.tareweight,
                            DOTNumber: savedTruck.dotnumber,
                            CompanyName: savedTruck.companyname,
                            Status: 'pending_sync'
                        };
                        
                        const updatedCache = [...cachedTrucks.data, newCachedTruck];
                        await window.electronAPI.secureStorage.setCachedRegisteredTrucks(updatedCache);
                        // console.log('✅ Truck cache updated with locally saved truck (error fallback)');
                    } catch (cacheError) {
                        console.error('⚠️ Failed to update truck cache:', cacheError);
                    }

                    alert(`Truck ${state.confirmedPlate} saved locally!\n\nIt will be synced to the server when possible.`);
                    
                    // Update sync status
                    const status = await window.electronAPI.secureStorage.getSyncQueueStatus();
                    setSyncStatus(status);
                    
                    // Reset state and navigate back to home
                    setState({
                        step: 1,
                        plateImage: null,
                        detectedPlate: '',
                        confirmedPlate: '',
                        truckNumber: '',
                        dotNumber: '',
                        companyName: '',
                        tareWeight: 0.00
                    });
                    onNavigate('home');
                }
            }
        }
    };

    const goBack = () => {
        if (state.step > 1) {
            setState({ ...state, step: state.step - 1 });
        } else {
            onNavigate('home');
        }
    };

    const retakePhoto = () => {
        setState({
            ...state,
            step: 1,
            plateImage: null,
            detectedPlate: '',
            confirmedPlate: '',
            truckNumber: '',
            dotNumber: '',
            companyName: '',
        });
    };

    return (
        <div className="screen">
            <div className="nav-breadcrumb">
                <a onClick={() => onNavigate('home')}>Home</a> / Register Truck
            </div>

            {/* Network Status Indicator */}
            {!isOnline && (
                <div style={{
                    background: '#ff6b6b',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <i className="fas fa-wifi" style={{ textDecoration: 'line-through' }}></i>
                    <strong>Offline Mode</strong> - Trucks will be saved locally and synced when connection is restored
                </div>
            )}

            {/* Sync Status Indicator */}
            {isOnline && syncStatus.hasPending && (
                <div style={{
                    background: '#4ecdc4',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fas fa-sync fa-spin"></i>
                        <span>
                            <strong>Syncing...</strong> {syncStatus.trucksCount} truck{syncStatus.trucksCount !== 1 ? 's' : ''} pending
                        </span>
                    </div>
                </div>
            )}

            <WizardSteps 
                steps={steps} 
                currentStep={state.step} 
                state={state} 
                setState={setState} 
            />

            {state.step === 1 && (
                        <div className='register-screen'>
                            {/* <h3>Primary Camera Feed</h3> */}
                            {primaryCamera ? (
                                <div className="camera-feed cam1" style={{
                                    minHeight: '300px',
                                    background: '#f5f5f5',
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    marginBottom: '0',
                                }}>
                                    <img
                                        data-camera-id={primaryCamera.id}
                                        data-src={primaryCamera.mjpegUrl}
                                        className="camera"
                                        alt="Primary Camera Feed"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            minHeight: '300px'
                                        }}
                                    />
                                    <div className="camera-overlay" style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        left: '0',
                                        right: '0',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        padding: '0.75rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        {primaryCamera.name || 'Primary Camera'}
                                    </div>
                                </div>
                            ) : (
                                <div className="camera-feed cam1" style={{
                                    minHeight: '300px',
                                    background: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    border: '2px dashed #6c757d'
                                }}>
                                    <div style={{ textAlign: 'center', color: '#6c757d' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}><i className="fas fa-video-slash"></i></div>
                                        <div><strong>No Camera Configured</strong></div>
                                        <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                            Camera is optional - you can still register trucks manually
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='guide'>
                                <h4>Instructions:</h4>
                                {isOnline ? (
                                    <ul style={{ fontSize: '0.875rem', color: '#4a5568', paddingLeft: '1.5rem' }}>
                                        <li>Ensure the truck is properly positioned on the scale</li>
                                        <li>Wait for the scale reading to stabilize</li>
                                        {primaryCamera ? (
                                            <>
                                                <li>Make sure the license plate is clearly visible to the camera</li>
                                                <li>Click "Capture License Plate" when ready</li>
                                            </>
                                        ) : (
                                            <li>Click "Continue to Details" to enter truck information manually (no camera configured)</li>
                                        )}
                                    </ul>
                                ) : (
                                    <ul style={{ fontSize: '0.875rem', color: '#4a5568', paddingLeft: '1.5rem' }}>
                                        <li>Ensure the truck is properly positioned on the scale</li>
                                        <li>Wait for the scale reading to stabilize (this will be the tare weight)</li>
                                        <li>Click "Continue to Details" to enter truck information manually</li>
                                        <li>Truck will be saved locally and synced when online</li>
                                    </ul>
                                )}
                            </div>
                            <div className='btn-cell'>
                                <button
                                    className="btn btn-success btn-large"
                                    onClick={captureImage}
                                    style={{ width: '100%' }}
                                    disabled={!devices.scale.connected}
                                >
                                    {isOnline ? (
                                        primaryCamera ? (
                                            <><i className="fas fa-camera"></i> Capture License Plate</>
                                        ) : (
                                            <><i className="fas fa-arrow-right"></i> Continue to Details</>
                                        )
                                    ) : (
                                        <><i className="fas fa-arrow-right"></i> Continue to Details</>
                                    )}
                                </button>
                            </div>

                            {/* {!devices.scale.connected && (
                                <p style={{ color: '#dc3545', textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem' }}>
                                    Scale must be connected to register trucks
                                </p>
                            )} */}

                            <div className="scale">
                                <div className="weight-display" style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: '1rem',
                                    background: '#333',
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef',
                                    margin: '0'
                                }}>
                                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}><i className="fas fa-weight-hanging"></i> Live Scale Reading</h3>
                                    {currentWeight ? (
                                        <div style={{
                                            fontSize: '2.5rem',
                                            fontWeight: 'bold',
                                            height: '50px',
                                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                        }}>
                                            {currentWeight}
                                            <span style={{
                                                fontSize: '1rem',
                                                opacity: 0.8
                                            }}>
                                                {/* {weightStatus === 'unstable' ? ' ⚠️' : ' ✅'} */}
                                            </span>
                                        </div>
                                    ) : (
                                        <div style={{
                                            fontSize: '1.5rem',
                                            opacity: 0.7,
                                            height: '50px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontStyle: 'italic'
                                        }}>
                                            Waiting for weight data...
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: '0.875rem',
                                        opacity: 0.8,
                                        marginTop: '0.5rem'
                                    }}>
                                        {weightStatus === 'unstable' ? 'Weight unstable - please wait for stable reading' : 'Weight stable'}
                                    </div>
                                </div>
                                <p style={{ color: '#718096', textAlign: 'center' }}>
                                    This will be recorded as the tare weight
                                </p>
                            </div>

                            {/* <div style={{ marginTop: '1rem' }}> */}
                            {configuredCameras.length > 0 && (
				configuredCameras.map((camera, index) => {
					const camNum = index+2;
					return (
					<div
						key={camera.id}
						className={`cam${camNum}`}
						style={{ 
							background: '#808080',
						 	borderRadius: '8px',
							border: '2px solid #e2e8f0',
							alignContent: 'center',
                            overflow: 'auto'
						}}
					>
						<div
							style={{
								width: '100%',
								overflow: 'hidden',
								background: '#f8f9fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								position: 'relative',
							}}
						>
							<img
								data-camera-id={camera.id}
								src=""
								alt={`Camera ${camera.name}`}
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'contain',
								}}
							/>
							<div
								className="camera-overlay"
								style={{
									position: 'absolute',
									bottom: '0',
									left: '0',
									right: '0',
									background: 'rgba(0,0,0,0.7)',
									color: 'white',
									padding: '0.75rem',
									fontSize: '0.875rem',
								}}
							>
								{camera.name || `Camera ${camera.id}`}
							</div>
						</div>
					</div>
				)})
			)}
                            {/* </div> */}
                        </div>
            )}

            {state.step === 2 && (
                <div>
                    <div className="grid-2">
                        <div>
                            <h3>Captured Image</h3>
                            {state.plateImage ? (
                                <div className="camera-feed" style={{
                                    minHeight: '300px',
                                    background: '#f5f5f5',
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={state.plateImage}
                                        alt="Captured License Plate"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            minHeight: '300px'
                                        }}
                                    />
                                    <div className="camera-overlay" style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        left: '0',
                                        right: '0',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        padding: '0.75rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        <i className="fas fa-camera"></i> Captured at {new Date().toLocaleString()}
                                    </div>
                                </div>
                            ) : (
                                <div className="camera-feed" style={{
                                    minHeight: '300px',
                                    background: isOnline ? '#f7fafc' : '#fff3cd',
                                    border: `2px dashed ${isOnline ? '#e2e8f0' : '#ffc107'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <div style={{ textAlign: 'center', color: isOnline ? '#718096' : '#856404' }}>
                                        {isOnline ? (
                                            <>
                                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}><i className="fas fa-camera"></i></div>
                                                <div>No Image Captured</div>
                                                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                                    Please go back and capture an image
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}><i className="fas fa-wifi" style={{ textDecoration: 'line-through' }}></i></div>
                                                <div><strong>Offline Mode</strong></div>
                                                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                                    Camera capture skipped - enter truck details manually
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {isOnline && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={retakePhoto}
                                    style={{ width: '100%', marginTop: '1rem' }}
                                >
                                    <i className="fas fa-camera"></i> Retake Photo
                                </button>
                            )}

                            {!isOnline && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem',
                                    background: '#d1ecf1',
                                    color: '#0c5460',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    border: '1px solid #bee5eb'
                                }}>
                                    <i className="fas fa-info-circle"></i> <strong>Offline Mode:</strong> Camera capture and OCR are unavailable. Please enter all truck details manually.
                                </div>
                            )}
                        </div>

                        <div>
                            <h3>Vehicle Information</h3>

                            <div className="form-group">
                                <label className="form-label">
                                    Detected License Plate *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    {state.isProcessing ? (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '1rem',
                                            padding: '1rem',
                                            fontSize: '1.25rem',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            letterSpacing: '2px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '8px',
                                            background: '#f8f9fa',
                                            color: '#667eea',
                                            minHeight: '60px'
                                        }}>
                                            <div className="loading-spinner" style={{
                                                width: '1.5rem',
                                                height: '1.5rem',
                                                border: '2px solid #e2e8f0',
                                                borderTop: '2px solid #667eea',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            {/* <span>OCR Processing...</span> */}
                                        </div>
                                    ) : (
                                        <input
                                            className="form-input"
                                            value={state.confirmedPlate}
                                            onChange={(e) => setState({ ...state, confirmedPlate: e.target.value.toUpperCase() })}
                                            placeholder="Enter license plate number"
                                            style={{
                                                fontSize: '1.25rem',
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                letterSpacing: '2px'
                                            }}
                                        />
                                    )}
                                </div>
                                <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                                    {state.isProcessing
                                        ? "Please wait while we process the license plate image..."
                                        : "Verify the detected license plate is correct"
                                    }
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Fleet/Truck Number
                                    <small style={{ color: '#718096', fontWeight: 'normal' }}> (Optional)</small>
                                </label>
                                <input
                                    className="form-input"
                                    value={state.truckNumber}
                                    onChange={(e) => setState({ ...state, truckNumber: e.target.value })}
                                    placeholder="T-001, Fleet-123, etc."
                                />
                                <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                                    Internal fleet identifier for easier truck management
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    DOT Number
                                    <small style={{ color: '#718096', fontWeight: 'normal' }}> (Optional)</small>
                                </label>
                                <input
                                    className="form-input"
                                    value={state.dotNumber}
                                    onChange={(e) => setState({ ...state, dotNumber: e.target.value })}
                                    placeholder="123456"
                                />
                                <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                                    US Department of Transportation identification number
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Company Name
                                    <small style={{ color: '#718096', fontWeight: 'normal' }}> (Optional)</small>
                                </label>
                                <input
                                    className="form-input"
                                    value={state.companyName}
                                    onChange={(e) => setState({ ...state, companyName: e.target.value })}
                                    placeholder="ABC Trucking Inc."
                                />
                                <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                                    Name of the trucking company or owner
                                </p>
                            </div>

                            {/* Additional Information Section */}
                            <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                <h4 style={{ marginBottom: '1rem' }}>Registration Summary</h4>
                                <div className="summary-table">
                                    <div className="summary-row">
                                        <span>License Plate:</span>
                                        <span style={{ fontWeight: 'bold' }}>{state.confirmedPlate || 'Not entered'}</span>
                                    </div>
                                    {/* <div className="summary-row">
                                        <span>Fleet Number:</span>
                                        <span>{state.truckNumber || 'Not specified'}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>DOT Number:</span>
                                        <span>{state.dotNumber || 'Not specified'}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Company Name:</span>
                                        <span>{state.companyName || 'Not specified'}</span>
                                    </div> */}
                                    <div className="summary-row">
                                        <span>Tare Weight:</span>
                                        <span style={{ fontWeight: 'bold' }}>{state.tareWeight.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Registration Date:</span>
                                        <span>{new Date().toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="wizard-actions">
                <button className="btn btn-secondary" onClick={goBack}>
                    {state.step === 1 ? 'Cancel' : 'Back'}
                </button>

                {state.step === 2 && (
                    <button
                        className="btn btn-success"
                        onClick={saveTruck}
                        disabled={!state.confirmedPlate.trim() || state.isProcessing}
                    >
                        {state.isProcessing ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>} Register Truck
                    </button>
                )}
            </div>
        </div>
    );
};

export default RegisterTruckScreen;